import { CommonModule } from '@angular/common';
import {
  Component,
  Input,
  OnDestroy,
  OnInit
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import {
  distinctUntilChanged,
  Subscription,
  take
} from 'rxjs';

import { PickListService } from '../../../../../core/shared/services/picklist.service';
import { PKL_GRP } from '../../../../../core/shared/models/picklist.model';
import { Popup } from '../../../../../core/shared/components/popup/popup';
import { CraditService } from '../../services/cradit.service';
import { ToastService } from '../../../../../core/shared/services/toast.service';
import { CdkDrag, CdkDragDrop, CdkDragHandle, CdkDropList } from '@angular/cdk/drag-drop';
import { SectionDragHandle } from '../section-drag-handle/section-drag-handle';
import { RichTextEditor } from '../rich-text-editor/rich-text-editor';
import { RichTextPipe } from '../../utils/rich-text.pipe';
import {
  bulletLinesToRichText,
  hasRichTextContent,
  richTextForSave,
} from '../../utils/rich-text.util';
import { moveVisibleItem } from '../../utils/section-reorder.util';
import {
  availableHighlightOptions,
  canAddHighlight,
  duplicateHighlightTitles,
} from '../../utils/highlight-titles.util';

@Component({
  selector: 'app-bullet-list-section',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    Popup,
    TranslatePipe,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    SectionDragHandle,
    RichTextEditor,
    RichTextPipe,
  ],
  templateUrl: './bullet-list-section.html',
  styleUrl: './bullet-list-section.css',
})
export class BulletListSection implements OnInit, OnDestroy {

  @Input() items: any[] = [];
  @Input() editable = false;

  onboardingId = 0;

  crdtPrpslSeq: number | null = null;
  crdtPrpslBlkSeq: number | null = null;

  highlightOptions: any[] = [];

  isLoading = false;
  isSaving = false;

  // Set on a save attempt so every required field shows its error,
  // not only the ones the user has already touched.
  showValidation = false;

  showDeletePopup = false;
  deleteMessage = '';
  private pendingDeleteIndex: number | null = null;

  private saveSub?: Subscription;
  private seqSub?: Subscription;

  constructor(
    private pickListService: PickListService,
    private creditService: CraditService,
    private route: ActivatedRoute,
    private translate: TranslateService,
    private toast: ToastService
  ) {}

  ngOnInit(): void {
    this.onboardingId = Number(
      this.getRouteParameter('id') ??
      this.getRouteParameter('oid') ??
      0
    );

    this.pickListService.loadCodes([
      PKL_GRP.HIGHLIGHT_TITLE
    ]);

    this.pickListService.picklists$
      .pipe(take(1))
      .subscribe(() => {
        this.highlightOptions =
          this.pickListService.getOptionsSync(
            PKL_GRP.HIGHLIGHT_TITLE
          );
      });

    this.seqSub = this.creditService.crdtPrpslSeq$
      .pipe(distinctUntilChanged())
      .subscribe((seq: number | null) => {
        const sequence = Number(seq ?? 0);

        if (sequence <= 0) {
          return;
        }

        const sequenceChanged =
          this.crdtPrpslSeq !== sequence;

        this.crdtPrpslSeq = sequence;

        if (
          sequenceChanged ||
          this.crdtPrpslBlkSeq == null
        ) {
          this.loadHighlights();
        }
      });

    this.saveSub =
      this.creditService.saveSectionTrigger$
        .subscribe((sectionId: string) => {
          if (sectionId === 'highlights') {
            this.saveHighlights();
          }
        });
  }

  ngOnDestroy(): void {
    this.saveSub?.unsubscribe();
    this.seqSub?.unsubscribe();
  }

  private getRouteParameter(
    parameterName: string
  ): string | null {
    let currentRoute: ActivatedRoute | null =
      this.route;

    while (currentRoute) {
      const value =
        currentRoute.snapshot.paramMap.get(
          parameterName
        );

      if (value != null) {
        return value;
      }

      currentRoute = currentRoute.parent;
    }

    return null;
  }

  loadHighlights(): void {
    const proposalSequence = Number(
      this.crdtPrpslSeq ?? 0
    );

    if (proposalSequence <= 0) {
      this.items = [];
      this.crdtPrpslBlkSeq = null;
      return;
    }

    this.isLoading = true;

    this.creditService
      .getCrdtBlck(proposalSequence, [2533])
      .pipe(take(1))
      .subscribe({
        next: (response: any) => {
          this.isLoading = false;

          const data =
            Array.isArray(response)
              ? response
              : Array.isArray(response?.data)
                ? response.data
                : Array.isArray(response?.blockList)
                  ? response.blockList
                  : Array.isArray(
                      response?.data?.blockList
                    )
                    ? response.data.blockList
                    : [];

          const matchingSections = data
            .filter(
              (section: any) =>
                Number(section?.crdtPrpslSeq) ===
                  proposalSequence &&
                Number(section?.refValSeq) === 2533 &&
                Number(section?.delFlg ?? 0) !== 1
            )
            .sort(
              (first: any, second: any) =>
                Number(
                  second?.crdtPrpslBlkSeq ?? 0
                ) -
                Number(
                  first?.crdtPrpslBlkSeq ?? 0
                )
            );

          /*
           * Always use the latest existing block.
           * For the supplied data this selects 197,
           * instead of old sequence 191.
           */
          const latestSection =
            matchingSections[0];

          if (!latestSection) {
            this.items = [];
            this.crdtPrpslBlkSeq = null;
            return;
          }

          const blockSequence = Number(
            latestSection?.crdtPrpslBlkSeq ?? 0
          );

          this.crdtPrpslBlkSeq =
            blockSequence > 0
              ? blockSequence
              : null;

          const jsonData = Array.isArray(
            latestSection?.jsonData
          )
            ? latestSection.jsonData
            : [];

          this.showValidation = false;

          this.items = jsonData
            .filter(
              (item: any) =>
                item?.code != null ||
                String(item?.title ?? '').trim() !== '' ||
                String(item?.content ?? '').trim() !== ''
            )
            .map((item: any) => ({
              code: item?.code ?? null,
              title: item?.title ?? '',
              // Highlights saved in the old bullet mode (one point per line)
              // open as an editor bullet list and are saved as rich text after.
              content:
                item?.showBullet !== false
                  ? bulletLinesToRichText(item?.content)
                  : item?.content ?? '',
              expanded: false
            }));

          console.log(
            'Loaded latest highlight block:',
            {
              crdtPrpslSeq:
                this.crdtPrpslSeq,
              crdtPrpslBlkSeq:
                this.crdtPrpslBlkSeq
            }
          );
        },

        error: (error: any) => {
          this.isLoading = false;

          console.error(
            'Unable to load highlights:',
            error
          );
        }
      });
  }

  // Shown in saved (ascending) order — new highlights are appended at the
  // bottom, and `number` matches the saved jsonData order and the report.
  get displayItems(): { item: any; number: number; index: number }[] {
    return this.items.map((item, index) => ({ item, number: index + 1, index }));
  }

  /** Drag-and-drop reorder; highlights are saved in array order. */
  onItemDrop(event: CdkDragDrop<unknown>): void {
    this.items = moveVisibleItem(this.items, () => true, event.previousIndex, event.currentIndex);
  }

  /** False once every highlight title is taken (titles are unique per proposal). */
  get canAddItem(): boolean {
    return canAddHighlight(this.highlightOptions, this.items);
  }

  /** Titles this highlight can pick: the ones no other highlight uses. */
  titleOptionsFor(item: any): any[] {
    return availableHighlightOptions(this.highlightOptions, this.items, item);
  }

  /** True when an earlier highlight already uses this highlight's title. */
  isTitleDuplicate(item: any): boolean {
    return duplicateHighlightTitles(this.items).has(item);
  }

  addItem(): void {
    if (!this.canAddItem) {
      return;
    }

    this.items.push({
      code: null,
      title: '',
      content: '',
      expanded: true
    });
  }

  requestRemoveItem(index: number): void {
    this.pendingDeleteIndex = index;
    this.deleteMessage = 'CREDIT_SECTION_HIGHLIGHTS.DELETE_CONFIRM';
    this.showDeletePopup = true;
  }

  confirmRemoveItem(): void {
    if (this.pendingDeleteIndex === null) {
      return;
    }

    this.removeItem(this.pendingDeleteIndex);
    this.pendingDeleteIndex = null;
  }

  private removeItem(index: number): void {
    if (!this.items[index]) {
      return;
    }

    this.items.splice(index, 1);
  }

  onHighlightChange(item: any): void {
    const selected =
      this.highlightOptions.find(
        option =>
          Number(option.value) ===
          Number(item.code)
      );

    item.title = selected?.label ?? '';
  }

  isTitleInvalid(item: any): boolean {
    return (
      item?.code == null &&
      String(item?.title ?? '').trim() === ''
    );
  }

  isContentInvalid(item: any): boolean {
    return !this.hasContent(item);
  }

  /** True when the highlight's rich text has visible text. */
  hasContent(item: any): boolean {
    return hasRichTextContent(item?.content);
  }

  isItemInvalid(item: any): boolean {
    return this.isTitleInvalid(item) || this.isContentInvalid(item);
  }

  showTitleError(item: any): boolean {
    return (this.showValidation || !!item?.titleTouched) && this.isTitleInvalid(item);
  }

  showContentError(item: any): boolean {
    return (this.showValidation || !!item?.contentTouched) && this.isContentInvalid(item);
  }

  saveHighlights(): void {
    // Prevent multiple simultaneous save requests
    if (this.isSaving) {
      return;
    }

    const proposalSequence = Number(
      this.crdtPrpslSeq ??
      this.creditService.getCrdtPrpslSeq() ??
      0
    );

    if (this.onboardingId <= 0) {
      console.error(
        'Onboarding sequence is not available'
      );
      this.creditService.reportSectionSaveResult({
        sectionId: 'highlights',
        status: 'validation-error',
        message: this.translate.instant('CREDIT_SECTION.ONBOARDING_SEQ_UNAVAILABLE'),
      });
      return;
    }

    if (proposalSequence <= 0) {
      console.error(
        'Credit proposal sequence is not available'
      );
      this.creditService.reportSectionSaveResult({
        sectionId: 'highlights',
        status: 'validation-error',
        message: this.translate.instant('CREDIT_SECTION.PROPOSAL_SEQ_UNAVAILABLE'),
      });
      return;
    }

    // Titles are unique per proposal; highlights saved with a repeated title
    // before this rule must be changed or deleted first.
    const duplicateItems = duplicateHighlightTitles(this.items);

    if (duplicateItems.size > 0) {
      duplicateItems.forEach((item: any) => (item.expanded = true));

      const message = this.translate.instant('CREDIT_SECTION_HIGHLIGHTS.DUPLICATE_TITLES_SAVE');

      this.toast.error(message);

      this.creditService.reportSectionSaveResult({
        sectionId: 'highlights',
        status: 'validation-error',
        message,
      });
      return;
    }

    const invalidItems = this.items.filter(
      (item: any) => this.isItemInvalid(item)
    );

    if (invalidItems.length > 0) {
      this.showValidation = true;
      invalidItems.forEach((item: any) => (item.expanded = true));

      const message = this.translate.instant('CREDIT_SECTION_HIGHLIGHTS.FIX_REQUIRED_FIELDS');

      this.toast.error(message);

      this.creditService.reportSectionSaveResult({
        sectionId: 'highlights',
        status: 'validation-error',
        message,
      });
      return;
    }

    // Every highlight title is required, so the report's Highlights sidebar
    // is always complete.
    const missingHighlights = availableHighlightOptions(
      this.highlightOptions,
      this.items,
      null
    );

    if (missingHighlights.length > 0) {
      this.showValidation = true;

      const message = this.translate.instant(
        'CREDIT_SECTION_HIGHLIGHTS.ALL_HIGHLIGHTS_REQUIRED',
        {
          titles: missingHighlights
            .map((option: any) => option.label)
            .join(', ')
        }
      );

      this.toast.error(message);

      this.creditService.reportSectionSaveResult({
        sectionId: 'highlights',
        status: 'validation-error',
        message,
      });
      return;
    }

    const jsonData = this.items
      .map((item: any) => ({
        code:
          item?.code != null
            ? Number(item.code)
            : null,

        title: String(
          item?.title ?? ''
        ).trim(),

        content: richTextForSave(item?.content),

        // Bullets now live inside the rich text (editor lists), so the old
        // bullet-mode flag is always saved off.
        showBullet: false
      }));

    // All highlights live in a single block, so the DB record is only
    // soft-deleted once every highlight has been removed.
    const isBlockDeleted = jsonData.length === 0;

    if (isBlockDeleted && !this.crdtPrpslBlkSeq) {
      // Nothing saved yet and nothing to save.
      this.creditService.reportSectionSaveResult({
        sectionId: 'highlights',
        status: 'saved',
      });
      return;
    }

    const payload = {
      crprtOnbrdngSeq: this.onboardingId,
      crdtPrpslSeq: proposalSequence,

      blockList: [
        {
          /*
           * Existing sequence will be sent here.
           * Backend should update this record instead
           * of creating a new record.
           */
          crdtPrpslBlkSeq:
            this.crdtPrpslBlkSeq,

          refGrp: 28,
          refValSeq: 2533,
          blkTyp: 3,
          delFlg: isBlockDeleted ? 1 : 0,
          sortOrdr: 1,
          txtData: null,
          jsonData
        }
      ]
    };

    console.log(
      'Saving highlights payload:',
      payload
    );

    this.isSaving = true;

    this.creditService
      .saveSection(payload)
      .pipe(take(1))
      .subscribe({
        next: (response: any) => {
          this.isSaving = false;

          const responseData =
            response?.data ?? response;

          const savedBlock =
            responseData?.blockList?.[0] ??
            responseData?.crdtPrpslBlkList?.[0] ??
            responseData;

          const returnedProposalSeq = Number(
            responseData?.crdtPrpslSeq ??
            savedBlock?.crdtPrpslSeq ??
            proposalSequence
          );

          const returnedBlockSeq = Number(
            savedBlock?.crdtPrpslBlkSeq ??
            responseData?.crdtPrpslBlkSeq ??
            this.crdtPrpslBlkSeq ??
            0
          );

          if (returnedProposalSeq > 0) {
            this.crdtPrpslSeq =
              returnedProposalSeq;

            this.creditService
              .setCrdtPrpslSeq(
                returnedProposalSeq
              );
          }

          if (returnedBlockSeq > 0) {
            this.crdtPrpslBlkSeq =
              returnedBlockSeq;
          }

          /*
           * Reload so the latest sequence from backend
           * remains available for the next save.
           */
          this.loadHighlights();

          this.creditService.reportSectionSaveResult({
            sectionId: 'highlights',
            status: 'saved',
          });
        },

        error: (error: any) => {
          this.isSaving = false;

          console.error(
            'Unable to save highlights:',
            error
          );

          this.creditService.reportSectionSaveResult({
            sectionId: 'highlights',
            status: 'api-error',
            message: error?.error?.message ?? this.translate.instant('CREDIT_SECTION_HIGHLIGHTS.SAVE_FAILED'),
          });
        }
      });
  }
}